<script setup lang="ts">
import MiniMetricTile from "@/components/Metrics/MiniMetricTile.vue";
import HollandCodePieChart from "@/pages/Insights/ReportsDetailPage/components/HollandCodePieChart.vue";

const props = defineProps(
  {
    reportData: {
      type: Object,
      required: true
    },
    showBreakdown: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  }
);

const content = {
  "R": {
    label: "Builder",
    icon: "fa fa-tools",
    color: "#fc631d"
  },
  "I": {
    label: "Thinker",
    icon: "fa fa-lightbulb",
    color: "#0487ff"
  },
  "A": {
    label: "Creator",
    icon: "fa fa-paint-brush",
    color: "#1eac30"
  },
  "S": {
    label: "Helper",
    icon: "fa fa-hands-helping",
    color: "#ff5d5d"
  },
  "E": {
    label: "Persuader",
    icon: "fa fa-bullhorn",
    color: "#514ee4"
  },
  "C": {
    label: "Organizer",
    icon: "fa fa-calendar-check",
    color: "#ff3520"
  }
};

function calculateRatio(value: number) {
  if (value === 0) return 0;
  return Math.round((value / props.reportData.total_count) * 100 * 100) / 100;
}
</script>

<template>
  <v-row v-if="loading">
    <v-col v-for="item in ['primary', 'secondary', 'tertiary']" :key="item" :cols="showBreakdown ? 12 : 4">
      <v-card variant="flat" class="metric-tile" height="100%">
        <v-card-text>
          <v-row>
            <v-col cols="auto">
              <v-skeleton-loader type="avatar" width="24" height="24"/>
            </v-col>
            <v-col cols="auto">
              <v-skeleton-loader type="heading" width="300"/>
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col :cols="showBreakdown ? 3 : 12">
              <v-skeleton-loader type="image" height="300"/>
            </v-col>
            <v-col v-if="showBreakdown" cols="9">
              <v-row>
                <v-col v-for="n in 6" :key="n" cols="4">
                  <v-skeleton-loader type="card" height="120"/>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <v-row v-else>
    <v-col v-for="item in ['primary', 'secondary', 'tertiary']" :key="item" :cols="showBreakdown ? 12 : 4">
      <v-card variant="flat" class="metric-tile" height="100%">
        <v-card-text>
          <v-row>
            <v-col cols="auto">
              <v-icon>far fa-pie-chart</v-icon>
            </v-col>
            <v-col cols="auto">
              <h2 v-if="item === 'primary'">Primary Worker Type Distribution</h2>
              <h2 v-else-if="item === 'secondary'">Secondary Worker Type Distribution</h2>
              <h2 v-else>Tertiary Worker Type Distribution</h2>
            </v-col>
          </v-row>

          <v-row v-if="reportData && reportData[item]">
            <v-col :cols="showBreakdown ? 3 : 12">
              <HollandCodePieChart  :chart-data="reportData[item]"/>
            </v-col>
            <v-col v-if="showBreakdown" cols="9">
              <v-row>
                <v-col :key="key" v-for="(value, key) in reportData[item]" cols="4">
                  <MiniMetricTile v-if="content[key as unknown as keyof typeof content]"
                                  :color="content[key as unknown as keyof typeof content].color"
                                  :progress-bar-value="calculateRatio(value)">
                    <template #icon>
                      {{ content[key as unknown as keyof typeof content].icon }}
                    </template>
                    <template #metric>
                      {{ value }}
                    </template>
                    <template #title>
                      {{ content[key as unknown as keyof typeof content].label }}
                    </template>
                    <template #comment>
                      {{ calculateRatio(value) }}% of Users
                    </template>
                  </MiniMetricTile>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<style scoped>

</style>
